/**
 * Elio's proposal formats, shared by the Worker (which records proposals the model makes) and
 * the app (which shows them and, after the traveller confirms, applies them through the normal,
 * permission-checked data paths). The model never changes data directly.
 */
import { z } from 'zod';
import type { Block } from './blocks';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM');
const tz = z.string().min(3).max(64).regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+\-]+)+$|^UTC$/, 'Use an IANA time zone such as Europe/Amsterdam');
const short = (n: number) => z.string().trim().min(1).max(n);
const opt = <T extends z.ZodTypeAny>(s: T) => s.nullish().transform((v) => v ?? null);
const money = z.string().trim().regex(/^\d{1,9}(\.\d{1,3})?$/, 'Write amounts like 149.50');
const currency = z.string().regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code');

export const EVENT_CATEGORIES = ['flight', 'rail', 'coach', 'ferry', 'cruise', 'road', 'rental', 'transfer', 'accommodation', 'activity', 'meal', 'meeting', 'free_time', 'other'] as const;
export const COST_CATEGORIES = ['transport', 'accommodation', 'food', 'activities', 'car', 'entry_requirements', 'insurance', 'connectivity', 'shopping', 'assistance', 'other'] as const;

export const stopSchema = z.object({
  city: short(80), country_code: opt(z.string().regex(/^[A-Z]{2}$/)), timezone: opt(tz),
  arrive_on: opt(isoDate), depart_on: opt(isoDate),
});

/** One itinerary entry in local wall time + IANA zone. The app converts to UTC (DST-aware) when applying. */
export const itemSchema = z.object({
  title: short(160),
  category: z.enum(EVENT_CATEGORIES),
  start_date: opt(isoDate), start_time: opt(hhmm), start_timezone: opt(tz),
  end_date: opt(isoDate), end_time: opt(hhmm), end_timezone: opt(tz),
  origin_name: opt(short(160)), destination_name: opt(short(160)),
  venue: opt(short(160)), address: opt(short(300)),
  operator: opt(short(120)), service_number: opt(short(40)),
  notes: opt(z.string().trim().max(1000)),
  status: z.enum(['planned', 'suggested']).default('planned'),
});

export const payloadSchemas = {
  create_trip: z.object({
    title: short(120), date_mode: z.enum(['exact', 'flexible', 'unknown']),
    start_date: opt(isoDate), end_date: opt(isoDate), date_note: opt(short(200)),
    origin_city: opt(short(80)), origin_timezone: opt(tz), currency: currency.default('EUR'),
    stops: z.array(stopSchema).max(20).default([]),
  }),
  update_trip: z.object({
    title: opt(short(120)), date_mode: opt(z.enum(['exact', 'flexible', 'unknown'])),
    start_date: opt(isoDate), end_date: opt(isoDate), date_note: opt(short(200)),
  }),
  set_stops: z.object({ stops: z.array(stopSchema).min(1).max(20) }),
  add_itinerary_items: z.object({ items: z.array(itemSchema).min(1).max(40) }),
  update_itinerary_item: z.object({ event_id: z.string().uuid(), changes: itemSchema.partial() }),
  remove_itinerary_item: z.object({ event_id: z.string().uuid(), title: short(160) }),
  add_booking: z.object({
    item: itemSchema,
    booking: z.object({
      provider_name: opt(short(120)), reference: opt(short(80)), price: opt(money), currency: opt(currency),
      fare_restrictions: opt(z.string().trim().max(2000)), cancellation_terms: opt(z.string().trim().max(2000)),
    }),
    /** Where the facts came from, and which fields Elio could not read with confidence. */
    source: z.enum(['traveller_message', 'uploaded_file']),
    uncertain_fields: z.array(short(60)).max(20).default([]),
    /** An existing planned/suggested itinerary item this booking turns into (it is removed when the booking is saved). */
    replaces_event_id: opt(z.string().min(1).max(64)),
  }),
  add_expense: z.object({
    amount: money, currency, occurred_on: isoDate, description: short(200),
    category: z.enum(COST_CATEGORIES).default('other'),
    split: z.enum(['just_me', 'equal_trip_members']).default('just_me'),
    source: z.enum(['traveller_message', 'uploaded_file']).default('traveller_message'),
  }),
  add_checklist_items: z.object({ items: z.array(z.object({ title: short(200), due_on: opt(isoDate) })).min(1).max(30) }),
  set_budget: z.object({
    currency, total_limit: opt(money),
    estimates: z.array(z.object({
      category: z.enum(COST_CATEGORIES), description: short(200), low: money, high: money,
      basis: short(300), source_url: opt(z.string().url().max(500)),
    })).max(30).default([]),
  }),
  /** Invite members of the trip's group to this trip (ids come from the context's group member list). */
  invite_to_trip: z.object({
    people: z.array(z.object({ user_id: z.string().uuid(), name: short(80) })).min(1).max(30),
    role: z.enum(['participant', 'guest']).default('participant'),
  }),
  /** Share a trip with one of the traveller's groups, so its members can be invited and plan together. */
  share_trip_with_group: z.object({ group_id: z.string().uuid(), group_name: short(80) }),
  /** Create a group, optionally inviting people by email straight away. */
  create_group: z.object({
    name: short(80), description: opt(short(500)),
    invite_emails: z.array(z.string().trim().toLowerCase().email().max(254)).max(10).default([]),
  }),
  /** Invite people to an existing group: by email, or (no emails) one shareable link. */
  invite_to_group: z.object({
    group_id: z.string().uuid(), group_name: short(80),
    emails: z.array(z.string().trim().toLowerCase().email().max(254)).max(10).default([]),
    role: z.enum(['member', 'admin']).default('member'),
  }),
} as const;

export type ActionKind = keyof typeof payloadSchemas;
export type ActionPayload<K extends ActionKind> = z.infer<(typeof payloadSchemas)[K]>;
export type ItemInput = z.infer<typeof itemSchema>;
export const ACTION_KINDS = Object.keys(payloadSchemas) as ActionKind[];
/** Kinds that need an existing trip. */
export const TRIP_ACTIONS: ActionKind[] = ACTION_KINDS.filter((k) => k !== 'create_trip' && k !== 'create_group' && k !== 'invite_to_group');

/**
 * Remove internal source markers some model replies contain (private-use characters such as
 * "\uE200cite\uE202turn0search1\uE201"). Real sources come separately as citations.
 */
export function stripSourceMarkers(text: string): string {
  return text
    .replace(/\uE200[^\uE201]*\uE201/g, '')
    .replace(/[\uE000-\uF8FF]/g, '')
    .replace(/[?&]utm_source=openai(?=[)\s\]]|$)/g, '')
    .replace(/[ \t]+([.,;:!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

export const MEMORY_KINDS = ['preference', 'decision', 'constraint', 'open_question', 'note'] as const;

export interface ChatAttachment { name: string; kind: 'image' | 'pdf'; dataUrl: string }
export interface ChatCitation { url: string; title: string }

export const chatRequestSchema = z.object({
  conversationId: z.string().uuid().nullish(),
  tripId: z.string().uuid().nullish(),
  text: z.string().trim().max(4000).default(''),
  attachments: z.array(z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(['image', 'pdf']),
    dataUrl: z.string().max(11_000_000).regex(/^data:(image\/(jpeg|png|webp|gif)|application\/pdf);base64,[A-Za-z0-9+/=]+$/, 'Unsupported attachment'),
  })).max(4).default([]),
  client: z.object({
    timeZone: z.string().max(64).nullish(),
    location: z.object({ lat: z.number().min(-90).max(90), lon: z.number().min(-180).max(180), accuracy_m: z.number().min(0).max(100_000).nullish() }).nullish(),
  }).default({}),
}).refine((r) => r.text.length > 0 || r.attachments.length > 0, { message: 'Write a message or attach a file.' });
export type ChatRequest = z.infer<typeof chatRequestSchema>;

export interface ChatAction {
  id: string; kind: ActionKind; summary: string; payload: Record<string, unknown>; status: 'proposed' | 'applied' | 'dismissed' | 'failed';
  trip_id: string | null; result: Record<string, unknown> | null; error: string | null;
}
export interface ChatMessage {
  id: string; role: 'user' | 'assistant'; text: string; created_at: string;
  attachments: { name: string; kind: 'image' | 'pdf' }[]; citations: ChatCitation[]; action_ids: string[];
  /** Visuals shown under an Elio message (display only). */
  blocks?: Block[];
}
export interface ChatResponse {
  conversationId: string;
  tripId: string | null;
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
  actions: ChatAction[];
}
